import type { ApiResponse, PaginatedResponse } from '@/shared/api/types/api-response'
import type {
  CreatePoiCategoryPayload,
  CreatePoiPayload,
  IPOI,
  IPoiAudio,
  IPoiCategory,
  PoiApiListResponse,
  PoiCategoryDTO,
  PoiCategoryListParams,
  POIDTO,
  PoiListParams,
  UpdatePoiCategoryPayload,
  UpdatePoiPayload,
} from '@/shared/api/types/poi-types'

import type { TranslationStatusType } from '@/shared/constants'
import { GW, toQueryString } from '@/shared/api/endpoints'
import {
  mapPoiCategoryDto,
  mapPoiCreatePayloadToRequest,
  mapPoiDtoToPoi,
  mapPoiUpdatePayloadToRequest,
} from '@/shared/api/mappers/poi-mapper'
import { buildLanguageHeaderConfig } from '@/shared/api/utils/language-header'
import { POI_CREATED_SOURCE_BACKEND_LABEL } from '@/shared/constants/poi-created-source'
import { request } from '@/shared/libs/request'

const POIS_PATH = 'admin/pois'
const POI_CATEGORIES_PATH = 'admin/poi-categories'
const POI_TYPES_PATH = 'public/pois/types'

async function getPoisByEndpoint(
  path: typeof POIS_PATH | `${typeof POIS_PATH}/all`,
  params: PoiListParams,
): Promise<PaginatedResponse<IPOI>> {
  const qs = toQueryString({
    current: params.current,
    pageSize: params.pageSize,
    ...(params.keyword ? { keyword: params.keyword } : {}),
    ...(params.isActive !== undefined ? { isActive: params.isActive } : {}),
    ...(params.category ? { category: params.category } : {}),
    ...(params.type ? { type: params.type } : {}),
    ...(params.provinceId ? { provinceId: params.provinceId } : {}),
    ...(params.createdSource
      ? { createdSource: POI_CREATED_SOURCE_BACKEND_LABEL[params.createdSource] }
      : {}),
    ...(params.tenantDestinationId ? { tenantDestinationId: params.tenantDestinationId } : {}),
  })
  const response = await request.get<{ data: PoiApiListResponse | null; message: string | null }>(
    GW.packaging(`${path}?${qs}`),
    buildLanguageHeaderConfig(params.languageCode),
  )
  if (!response.data.data) {
    return {
      data: [],
      total: 0,
      current: params.current,
      keyword: params.keyword ?? null,
      pageSize: params.pageSize,
    }
  }

  const mappedItems = response.data.data.data.map(mapPoiDtoToPoi)

  return {
    data: mappedItems,
    total: response.data.data.total,
    current: params.current,
    keyword: params.keyword ?? null,
    pageSize: params.pageSize,
  }
}

export async function getPois(params: PoiListParams): Promise<PaginatedResponse<IPOI>> {
  return getPoisByEndpoint(POIS_PATH, params)
}

export async function getPoisSuperadmin(params: PoiListParams): Promise<PaginatedResponse<IPOI>> {
  return getPoisByEndpoint(`${POIS_PATH}/all`, params)
}

export async function getPoiCategories(
  params: PoiCategoryListParams = {},
): Promise<PaginatedResponse<IPoiCategory>> {
  const qs = toQueryString({
    ...(params.current !== undefined ? { current: params.current } : {}),
    ...(params.pageSize !== undefined ? { pageSize: params.pageSize } : {}),
    ...(params.keyword ? { keyword: params.keyword } : {}),
  })
  const response = await request.get<{
    data: { data: PoiCategoryDTO[] | null; total: number; current: number; pageSize: number } | null
    message: string | null
  }>(
    GW.packaging(qs ? `${POI_CATEGORIES_PATH}?${qs}` : POI_CATEGORIES_PATH),
    buildLanguageHeaderConfig(params.languageCode),
  )

  const inner = response.data.data
  return {
    data: (inner?.data ?? []).map(mapPoiCategoryDto),
    total: inner?.total ?? 0,
    current: inner?.current ?? params.current ?? 1,
    pageSize: inner?.pageSize ?? params.pageSize ?? 10,
  }
}

export async function getPoiCategoryById(
  id: string,
  languageCode?: string | null,
): Promise<ApiResponse<IPoiCategory>> {
  const response = await request.get<
    { data?: PoiCategoryDTO | null; message?: string | null } | undefined
  >(GW.packaging(`${POI_CATEGORIES_PATH}/${id}`), buildLanguageHeaderConfig(languageCode))
  if (!response.data?.data) {
    throw new Error(response.data?.message ?? 'API returned empty data')
  }

  return {
    data: mapPoiCategoryDto(response.data.data),
    ...(response.data.message ? { message: response.data.message } : {}),
  }
}

export async function createPoiCategory(
  payload: CreatePoiCategoryPayload,
): Promise<ApiResponse<IPoiCategory | null>> {
  const response = await request.post<
    { data?: PoiCategoryDTO | null; message?: string | null } | undefined
  >(GW.packaging(POI_CATEGORIES_PATH), payload)

  return {
    data: response.data?.data ? mapPoiCategoryDto(response.data.data) : null,
    message: response.data?.message ?? 'POI category created successfully',
  }
}

export async function updatePoiCategory(
  id: string,
  payload: UpdatePoiCategoryPayload,
): Promise<ApiResponse<IPoiCategory | null>> {
  const response = await request.patch<
    { data?: PoiCategoryDTO | null; message?: string | null } | undefined
  >(GW.packaging(`${POI_CATEGORIES_PATH}/${id}`), payload)

  return {
    data: response.data?.data ? mapPoiCategoryDto(response.data.data) : null,
    message: response.data?.message ?? 'POI category updated successfully',
  }
}

export async function deletePoiCategory(id: string): Promise<ApiResponse<null>> {
  await request.delete(GW.packaging(`${POI_CATEGORIES_PATH}/${id}`))
  return {
    data: null,
    message: 'POI category deleted successfully',
  }
}

export async function getPoiTypes(languageCode?: string | null): Promise<ApiResponse<string[]>> {
  const response = await request.get<{ data: string[] | null }>(
    GW.packaging(POI_TYPES_PATH),
    buildLanguageHeaderConfig(languageCode),
  )
  return {
    data: response.data.data ?? [],
  }
}

export async function getPoiById(
  id: string,
  languageCode?: string | null,
): Promise<ApiResponse<IPOI>> {
  const response = await request.get<{ data: POIDTO | null; message: string | null }>(
    GW.packaging(`${POIS_PATH}/${id}`),
    buildLanguageHeaderConfig(languageCode),
  )
  if (!response.data.data) {
    throw new Error(response.data.message ?? 'API returned empty data')
  }
  return {
    data: mapPoiDtoToPoi(response.data.data),
  }
}

export async function createPoi(payload: CreatePoiPayload): Promise<ApiResponse<IPOI>> {
  const response = await request.post<{ data: POIDTO | null; message: string | null }>(
    GW.packaging(POIS_PATH),
    mapPoiCreatePayloadToRequest(payload),
  )
  if (!response.data.data) {
    throw new Error(response.data.message ?? 'API returned empty data')
  }

  return {
    data: mapPoiDtoToPoi(response.data.data),
    message: 'POI created successfully',
  }
}

export async function updatePoi(id: string, payload: UpdatePoiPayload): Promise<ApiResponse<null>> {
  await request.patch(GW.packaging(`${POIS_PATH}/${id}`), mapPoiUpdatePayloadToRequest(payload))
  return { data: null, message: 'POI updated successfully' }
}

export async function deletePoi(id: string): Promise<ApiResponse<null>> {
  await request.delete(GW.packaging(`${POIS_PATH}/${id}`))
  return {
    data: null,
    message: 'POI deleted successfully',
  }
}

export async function uploadPoiImage(
  id: string,
  imageFile: File,
  index?: number,
): Promise<ApiResponse<IPOI>> {
  const formData = new FormData()
  formData.append('image_file', imageFile)

  const qs = index !== undefined ? `?${toQueryString({ index })}` : ''
  const response = await request.post<{ data: POIDTO | null; message: string | null }>(
    GW.packaging(`${POIS_PATH}/${id}/images${qs}`),
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  if (!response.data.data) {
    throw new Error(response.data.message ?? 'API returned empty data')
  }

  return {
    data: mapPoiDtoToPoi(response.data.data),
    message: 'POI image uploaded successfully',
  }
}

export async function reorderPoiImages(id: string, newIndex: number[]): Promise<ApiResponse<null>> {
  const sp = new URLSearchParams()
  newIndex.forEach((i) => sp.append('new_index', String(i)))

  await request.patch(GW.packaging(`${POIS_PATH}/${id}/reorder-images?${sp.toString()}`))

  return {
    data: null,
    message: 'POI images reordered successfully',
  }
}

export async function generatePoiTts(
  _id: string,
  _language: string,
): Promise<ApiResponse<IPoiAudio>> {
  throw new Error('POI text-to-speech endpoint is not available yet')
}

export async function uploadPoiAudio(
  _id: string,
  _audio: Omit<IPoiAudio, 'id'>,
): Promise<ApiResponse<IPoiAudio>> {
  throw new Error('POI audio upload endpoint is not available yet')
}

export async function updateTranslationStatus(
  poiId: string,
  language: string,
  status: TranslationStatusType,
): Promise<ApiResponse<IPOI>> {
  const poiResponse = await getPoiById(poiId)

  return {
    data: {
      ...poiResponse.data,
      localizedData: poiResponse.data.localizedData.map((translation) =>
        translation.language === language ? { ...translation, status } : translation,
      ),
    },
    message: 'Translation status update is currently local-only',
  }
}

export async function bulkUpdateTranslationStatus(
  poiIds: string[],
  language: string,
  status: TranslationStatusType,
): Promise<ApiResponse<null>> {
  await Promise.all(poiIds.map(async (poiId) => updateTranslationStatus(poiId, language, status)))

  return {
    data: null,
    message: 'Bulk translation status update is currently local-only',
  }
}
